import { useState, useMemo, useEffect, useCallback } from "react";
import I from "./Icons";
import Dropdown from "./Dropdown";
import PageCard from "./PageCard";
import { P, G } from "./constants";
import { apiGet, apiPost } from "../../../../utils/apiClient";
import { useTables, useTablesReady } from "../report-builder";

interface SectionProps {
  section: any;
  onUpdate: (section: any) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  theme: any;
}

// ─── RepeatRow ──────────────────────────────────────────────────────────────

interface RepeatRowProps {
  rep: { field: string; values: string[] | null };
  index: number;
  repeatOptions: string[];
  theme: any;
  onFieldChange: (field: string) => void;
  onValuesChange: (values: string[] | null) => void;
  onRemove: () => void;
}

function RepeatRow({
  rep,
  index,
  repeatOptions,
  theme,
  onFieldChange,
  onValuesChange,
  onRemove,
}: RepeatRowProps) {
  const tablesReady = useTablesReady();
  const [availableValues, setAvailableValues] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Fetch unique values across ALL tables that contain this column,
  // but only once the datasource has been loaded
  useEffect(() => {
    if (!tablesReady) {
      return;
    }
    if (!rep.field || rep.field === "None") {
      setAvailableValues([]);
      setFetchError(null);
      return;
    }
    const col = rep.field.toLowerCase().replace(/ /g, "_");
    setLoading(true);
    setFetchError(null);
    
    // Import ReportBuilderAPI dynamically to avoid circular dependencies
    import("../../../../utils/ReportBuilderAPI").then(({ ReportBuilderAPI }) => {
      ReportBuilderAPI.getUniqueValuesGlobal(col)
        .then((data) => {
          setAvailableValues(data.values || []);
        })
        .catch((err) => {
          setAvailableValues([]);
          setFetchError(err?.message ?? "Failed to load values");
        })
        .finally(() => setLoading(false));
    });
  }, [tablesReady, rep.field]);

  // Defensively ensure selectedValues is always null or a real array
  const selectedValues: string[] | null =
    rep.values === null || rep.values === undefined
      ? null
      : Array.isArray(rep.values)
      ? rep.values
      : null;

  const isAllSelected =
    selectedValues === null ||
    (availableValues.length > 0 &&
      availableValues.every((v) => selectedValues.includes(v)));

  // Ordered list of selected values (preserves user ordering)
  const orderedSelected: string[] = useMemo(() => {
    if (selectedValues === null) return [...availableValues];
    return selectedValues.filter((v) => availableValues.includes(v));
  }, [selectedValues, availableValues]);

  const unselected: string[] = useMemo(
    () => availableValues.filter((v) => !orderedSelected.includes(v)),
    [availableValues, orderedSelected],
  );

  const toggleAll = () => {
    if (isAllSelected) {
      onValuesChange([]);
    } else {
      onValuesChange(null); // null = all
    }
  };

  const addValue = (v: string) => {
    const next = [...orderedSelected, v];
    // Always store explicit array to preserve user ordering
    onValuesChange(next);
  };

  const removeValue = (v: string) => {
    if (selectedValues === null) {
      onValuesChange(availableValues.filter((x) => x !== v));
    } else {
      const next = selectedValues.filter((x) => x !== v);
      onValuesChange(next.length === 0 ? [] : next);
    }
  };

  // ── Drag-to-reorder state ──
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dropIdx, setDropIdx] = useState<number | null>(null);

  const onDragStart = (idx: number) => (e: React.DragEvent) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropIdx(idx);
  };

  const onDrop = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDropIdx(null); return; }
    const reordered = [...orderedSelected];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    setDragIdx(null);
    setDropIdx(null);
    onValuesChange(reordered);
  };

  const onDragEnd = () => { setDragIdx(null); setDropIdx(null); };

  const hasField = rep.field && rep.field !== "None";

  return (
    <div
      style={{
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: "8px 10px",
        background: theme.background,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      {/* ── Row header: field dropdown + remove button ── */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Dropdown
            label={index === 0 ? "Repeat Section By" : `Repeat Field ${index + 1}`}
            value={rep.field ?? "None"}
            options={repeatOptions}
            onChange={(v) => onFieldChange(v)}
            style={{ width: "100%" }}
            theme={theme}
          />
        </div>
        <button
          onClick={onRemove}
          title="Remove repeat field"
          style={{
            background: "none",
            border: "none",
            color: theme.textSecondary,
            cursor: "pointer",
            padding: "4px 6px",
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            marginBottom: 1,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = theme.error)}
          onMouseLeave={(e) => (e.currentTarget.style.color = theme.textSecondary)}
        >
          <I.Trash />
        </button>
      </div>

      {/* ── Value checkboxes (only when a field is selected) ── */}
      {hasField && (
        <div style={{ paddingLeft: 2 }}>
          {!tablesReady ? (
            <span style={{ fontSize: 11, color: theme.textSecondary, fontStyle: "italic" }}>
              Load a datasource to filter values
            </span>
          ) : loading ? (
            <span style={{ fontSize: 11, color: theme.textSecondary }}>
              Loading values…
            </span>
          ) : fetchError ? (
            <span style={{ fontSize: 11, color: theme.error ?? "#e57373" }} title={fetchError}>
              ⚠ Could not load values
            </span>
          ) : availableValues.length === 0 ? (
            <span style={{ fontSize: 11, color: theme.textSecondary }}>
              No values found
            </span>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {/* ── "All" toggle ── */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 11,
                  color: theme.textSecondary,
                  cursor: "pointer",
                  userSelect: "none",
                  fontWeight: 700,
                }}
              >
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={toggleAll}
                  style={{ accentColor: theme.blue, width: 12, height: 12 }}
                />
                All
              </label>

              {/* ── Selected values (draggable, ordered) ── */}
              {orderedSelected.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {orderedSelected.map((v, i) => (
                    <div
                      key={v}
                      draggable
                      onDragStart={onDragStart(i)}
                      onDragOver={onDragOver(i)}
                      onDrop={onDrop(i)}
                      onDragEnd={onDragEnd}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 11,
                        padding: "2px 6px 2px 8px",
                        borderRadius: 10,
                        background: theme.blue + "1A",
                        color: theme.blue,
                        border: `1px solid ${dropIdx === i && dragIdx !== i ? theme.blue : "transparent"}`,
                        cursor: "grab",
                        userSelect: "none",
                        opacity: dragIdx === i ? 0.4 : 1,
                        transition: "border 0.1s, opacity 0.1s",
                      }}
                    >
                      <span style={{ cursor: "grab", marginRight: 2, opacity: 0.5, fontSize: 9 }}>⠿</span>
                      {v}
                      <span
                        onClick={() => removeValue(v)}
                        style={{
                          cursor: "pointer",
                          marginLeft: 2,
                          opacity: 0.6,
                          fontSize: 13,
                          lineHeight: 1,
                        }}
                        title="Remove"
                      >
                        ×
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Unselected values (click to add) ── */}
              {unselected.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {unselected.map((v) => (
                    <div
                      key={v}
                      onClick={() => addValue(v)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        fontSize: 11,
                        padding: "2px 8px",
                        borderRadius: 10,
                        background: theme.background,
                        color: theme.textSecondary,
                        border: `1px dashed ${theme.border}`,
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      + {v}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Section ─────────────────────────────────────────────────────────────────

function Section({
  section,
  onUpdate,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  theme,
}: SectionProps) {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const TABLES = useTables();

  // Build dynamic repeat options from all text fields across all tables
  const REPEATS = useMemo(() => {
    const textFieldsSet = new Set<string>();
    TABLES.forEach((table: any) => {
      if (table.text_fields && Array.isArray(table.text_fields)) {
        table.text_fields.forEach((field: string) => {
          textFieldsSet.add(field);
        });
      }
    });
    const formattedFields = Array.from(textFieldsSet)
      .sort()
      .map((field: string) =>
        field.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())
      );
    return ["None", ...formattedFields];
  }, [TABLES]);

  // ── Repeats helpers ────────────────────────────────────────────────
  const repeats: { field: string; values: string[] | null }[] =
    Array.isArray(section.repeats) ? section.repeats : [];

  const addRepeat = useCallback(() => {
    onUpdate({
      ...section,
      repeats: [...repeats, { field: "None", values: null }],
    });
  }, [section, repeats, onUpdate]);

  const removeRepeat = useCallback(
    (idx: number) => {
      onUpdate({
        ...section,
        repeats: repeats.filter((_: any, i: number) => i !== idx),
      });
    },
    [section, repeats, onUpdate]
  );

  const updateRepeat = useCallback(
    (idx: number, patch: Partial<{ field: string; values: string[] | null }>) => {
      onUpdate({
        ...section,
        repeats: repeats.map((r: any, i: number) =>
          i === idx ? { ...r, ...patch } : r
        ),
      });
    },
    [section, repeats, onUpdate]
  );

  // ── Filter helpers ─────────────────────────────────────────────────
  const filter: { field: string; values: string[] | null } | null =
    section.filter || null;

  const setFilter = useCallback(
    (newFilter: { field: string; values: string[] | null } | null) => {
      onUpdate({ ...section, filter: newFilter });
    },
    [section, onUpdate]
  );

  const updateFilter = useCallback(
    (patch: Partial<{ field: string; values: string[] | null }>) => {
      if (!filter) return;
      onUpdate({ ...section, filter: { ...filter, ...patch } });
    },
    [section, filter, onUpdate]
  );

  // ── Derived state ──────────────────────────────────────────────────
  const isRep = repeats.some(
    (r) => r.field && r.field !== "None"
  );

  const hasFilter = filter && filter.field && filter.field !== "None";

  const repeatBadgeLabel = useMemo(() => {
    const active = repeats.filter((r) => r.field && r.field !== "None");
    if (active.length === 0) return null;
    if (active.length === 1) return active[0].field.toUpperCase();
    return `${active.length} FIELDS`;
  }, [repeats]);

  // ── Page helpers ───────────────────────────────────────────────────
  const upP = (pid: string, d: any) =>
    onUpdate({
      ...section,
      pages: section.pages.map((p: any) => (p.id === pid ? d : p)),
    });
  const delP = (pid: string) => {
    if (section.pages.length <= 1) return;
    onUpdate({ ...section, pages: section.pages.filter((p: any) => p.id !== pid) });
  };
  const moveP = (pid: string, dir: number) => {
    const pages = [...section.pages];
    const i = pages.findIndex((p: any) => p.id === pid);
    const j = i + dir;
    if (j < 0 || j >= pages.length) return;
    [pages[i], pages[j]] = [pages[j], pages[i]];
    onUpdate({ ...section, pages });
  };
  const dupP = (pid: string) => {
    const pages = [...section.pages];
    const i = pages.findIndex((p: any) => p.id === pid);
    const copy = JSON.parse(JSON.stringify(pages[i]));
    copy.id = require("./constants").uid();
    copy.name += " (Copy)";
    copy.graphs.forEach((g: any) => g.id = require("./constants").uid());
    pages.splice(i + 1, 0, copy);
    onUpdate({ ...section, pages });
  };
  const addP = () => {
    // Use first valid table and field from loaded datasource
    const firstTable = TABLES.length > 0 ? TABLES[0].id : "revenue";
    const firstField = TABLES.length > 0 && TABLES[0].numeric_fields?.length > 0 
      ? TABLES[0].numeric_fields[0] 
      : "revenue_amount";
    onUpdate({
      ...section,
      pages: [
        ...section.pages,
        P(
          "Page " + (section.pages.length + 1),
          [G("Graph 1", "stacked_bar", firstTable, firstField)],
          "1",
          "none"
        ),
      ],
    });
  };

  const totalGraphs = section.pages.reduce(
    (a: number, p: any) => a + p.graphs.length,
    0
  );

  const saveAsTemplate = async () => {
    try {
      const response = await apiPost("/api/reporting/templates/sections", section);
      const result = await response.json();
      if (response.ok) {
        alert(`Template "${section.name}" saved successfully!`);
      } else {
        alert(`Failed to save template: ${result.detail || "Unknown error"}`);
      }
    } catch (error: any) {
      alert(`Error saving template: ${error.message}`);
    }
  };

  // ── Action buttons ─────────────────────────────────────────────────
  const actionButtons = (
    <div style={{ display: "flex", gap: 1, flexShrink: 0 }}>
      <button
        onClick={onMoveUp}
        disabled={isFirst}
        style={{
          background: "none",
          border: "none",
          color: isFirst ? theme.border : theme.textSecondary,
          cursor: isFirst ? "default" : "pointer",
          padding: 3,
          display: "flex",
        }}
      >
        <I.Up />
      </button>
      <button
        onClick={onMoveDown}
        disabled={isLast}
        style={{
          background: "none",
          border: "none",
          color: isLast ? theme.border : theme.textSecondary,
          cursor: isLast ? "default" : "pointer",
          padding: 3,
          display: "flex",
        }}
      >
        <I.Dn />
      </button>
      <button
        onClick={onDuplicate}
        title="Duplicate section"
        style={{
          background: "none",
          border: "none",
          color: theme.textSecondary,
          cursor: "pointer",
          padding: 3,
          display: "flex",
        }}
      >
        <I.Copy />
      </button>
      <button
        onClick={async (e) => {
          e.stopPropagation();
          try {
            const data = JSON.stringify({ _type: "zell_section", section });
            await navigator.clipboard.writeText(data);
          } catch {
            const ta = document.createElement("textarea");
            ta.value = JSON.stringify({ _type: "zell_section", section });
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
          }
        }}
        title="Copy to clipboard"
        style={{
          background: "none",
          border: "none",
          color: theme.textSecondary,
          cursor: "pointer",
          padding: 3,
          display: "flex",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = theme.blue)}
        onMouseLeave={(e) => (e.currentTarget.style.color = theme.textSecondary)}
      >
        <I.Clipboard />
      </button>
      <button
        onClick={saveAsTemplate}
        title="Save as Template"
        style={{
          background: "none",
          border: "none",
          color: theme.textSecondary,
          cursor: "pointer",
          padding: 3,
          display: "flex",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = theme.blue)}
        onMouseLeave={(e) => (e.currentTarget.style.color = theme.textSecondary)}
      >
        <I.Save />
      </button>
      <button
        onClick={onDelete}
        style={{
          background: "none",
          border: "none",
          color: theme.textSecondary,
          cursor: "pointer",
          padding: 3,
          display: "flex",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = theme.error)}
        onMouseLeave={(e) => (e.currentTarget.style.color = theme.textSecondary)}
      >
        <I.Trash />
      </button>
    </div>
  );

  return (
    <div
      style={{
        background: theme.cardBackground,
        borderRadius: 14,
        overflow: "visible",
        border: `1px solid ${isRep ? theme.blue : theme.border}`,
        ...(isRep ? { boxShadow: `0 0 0 1px ${theme.blue}33` } : {}),
      }}
    >
      {collapsed ? (
        /* ── Collapsed ── */
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "9px 14px",
            cursor: "pointer",
            userSelect: "none",
            ...(isRep
              ? {
                  background: `linear-gradient(135deg, ${theme.blue}11, transparent)`,
                  borderRadius: 14,
                }
              : {}),
          }}
          onClick={() => setCollapsed(false)}
        >
          <span style={{ color: theme.textSecondary, flexShrink: 0 }}>
            <I.Right />
          </span>
          <span
            style={{
              color: theme.textSecondary,
              flexShrink: 0,
              cursor: "grab",
            }}
          >
            <I.Grip />
          </span>
          <span
            style={{
              flex: 1,
              fontSize: 15,
              fontWeight: 700,
              color: theme.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {section.name}
          </span>
          {isRep && repeatBadgeLabel && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                padding: "2px 8px",
                background: theme.blue,
                borderRadius: 12,
                fontSize: 11,
                fontWeight: 700,
                color: "white",
                whiteSpace: "nowrap",
              }}
            >
              <I.Loop /> {repeatBadgeLabel}
            </span>
          )}
          <span
            style={{ fontSize: 12, color: theme.textSecondary, whiteSpace: "nowrap" }}
          >
            {section.pages.length} page{section.pages.length !== 1 ? "s" : ""} ·{" "}
            {totalGraphs} graphs
          </span>
          <div onClick={(e) => e.stopPropagation()}>{actionButtons}</div>
        </div>
      ) : (
        /* ── Expanded header ── */
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "14px 16px 10px",
            ...(isRep
              ? {
                  background: `linear-gradient(135deg, ${theme.blue}11, transparent)`,
                }
              : {}),
          }}
        >
          <button
            onClick={() => setCollapsed(true)}
            style={{
              background: "none",
              border: "none",
              color: theme.textSecondary,
              cursor: "pointer",
              padding: "3px 0",
              marginTop: 1,
              display: "flex",
              flexShrink: 0,
            }}
          >
            <I.Down />
          </button>
          <div
            style={{
              color: theme.textSecondary,
              cursor: "grab",
              padding: "3px 0",
              marginTop: 1,
              flexShrink: 0,
            }}
          >
            <I.Grip />
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}
            >
              <input
                value={section.name}
                onChange={(e) => onUpdate({ ...section, name: e.target.value })}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  color: theme.text,
                  fontSize: 17,
                  fontWeight: 700,
                  padding: 0,
                  outline: "none",
                }}
              />
              {isRep && repeatBadgeLabel && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    padding: "2px 8px",
                    background: theme.blue,
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 700,
                    color: "white",
                    whiteSpace: "nowrap",
                  }}
                >
                  <I.Loop /> REPEATS BY {repeatBadgeLabel}
                </div>
              )}
            </div>
            <input
              value={section.description}
              onChange={(e) => onUpdate({ ...section, description: e.target.value })}
              placeholder="Section description..."
              style={{
                background: "transparent",
                border: "none",
                color: theme.textSecondary,
                fontSize: 13,
                padding: 0,
                outline: "none",
                width: "100%",
              }}
            />
          </div>
          {actionButtons}
        </div>
      )}

      {!collapsed && (
        <>
          {/* ── Repeat fields section ── */}
          <div
            style={{
              padding: "0 16px 10px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: theme.textSecondary,
              }}
            >
              Repeat Section By
            </span>

            {repeats.map((rep, idx) => (
              <RepeatRow
                key={idx}
                rep={rep}
                index={idx}
                repeatOptions={REPEATS}
                theme={theme}
                onFieldChange={(field) =>
                  updateRepeat(idx, { field, values: null })
                }
                onValuesChange={(values) => updateRepeat(idx, { values })}
                onRemove={() => removeRepeat(idx)}
              />
            ))}

            {/* Add repeat field button */}
            <button
              onClick={addRepeat}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 5,
                padding: "7px",
                borderRadius: 8,
                cursor: "pointer",
                background: "none",
                border: `1.5px dashed ${theme.border}`,
                color: theme.textSecondary,
                fontSize: 12,
                fontWeight: 500,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = theme.blue;
                e.currentTarget.style.color = theme.blue;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.border;
                e.currentTarget.style.color = theme.textSecondary;
              }}
            >
              <I.Plus /> Add Repeat Field
            </button>

            {/* Summary line when multiple repeats active */}
            {repeats.filter((r) => r.field && r.field !== "None").length > 1 && (
              <div style={{ fontSize: 12, color: theme.textSecondary, paddingLeft: 2 }}>
                ↳ Generates per{" "}
                <strong style={{ color: theme.blue }}>
                  {repeats
                    .filter((r) => r.field && r.field !== "None")
                    .map((r) => r.field)
                    .join(" × ")}
                </strong>
              </div>
            )}
          </div>

          {/* ── Filter section ── */}
          <div
            style={{
              padding: "0 16px 10px",
              borderTop: `1px solid ${theme.border}`,
              paddingTop: 10,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: theme.textSecondary,
              }}
            >
              Filter Section Data
            </span>

            {filter ? (
              <RepeatRow
                rep={filter}
                index={0}
                repeatOptions={REPEATS}
                theme={theme}
                onFieldChange={(field) => updateFilter({ field, values: null })}
                onValuesChange={(values) => updateFilter({ values })}
                onRemove={() => setFilter(null)}
              />
            ) : (
              <button
                onClick={() => setFilter({ field: "None", values: null })}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                  padding: "7px",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: "none",
                  border: `1.5px dashed ${theme.border}`,
                  color: theme.textSecondary,
                  fontSize: 12,
                  fontWeight: 500,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.blue;
                  e.currentTarget.style.color = theme.blue;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.color = theme.textSecondary;
                }}
              >
                <I.Plus /> Add Filter
              </button>
            )}

            {hasFilter && (
              <div style={{ fontSize: 12, color: theme.textSecondary, paddingLeft: 2 }}>
                ↳ Filters all graphs by{" "}
                <strong style={{ color: theme.blue }}>{filter!.field}</strong>
              </div>
            )}
          </div>

          {/* ── Divider Page Toggle ── */}
          <div
            style={{
              padding: "0 16px 14px",
              borderTop: `1px solid ${theme.border}`,
              paddingTop: 14,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 500,
                color: theme.text,
              }}
            >
              <input
                type="checkbox"
                checked={section.includeDividerPage || false}
                onChange={(e) =>
                  onUpdate({ ...section, includeDividerPage: e.target.checked })
                }
                style={{ cursor: "pointer" }}
              />
              <span>Include Divider Page</span>
              <span
                style={{
                  fontSize: 11,
                  color: theme.textSecondary,
                  fontWeight: 400,
                }}
              >
                (shows section divider image from branding)
              </span>
            </label>
          </div>

          {/* ── Pages ── */}
          <div
            style={{ padding: "0 16px 14px", display: "flex", flexDirection: "column", gap: 8 }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: theme.textSecondary,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>Pages ({section.pages.length})</span>
              <span
                style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0 }}
              >
                {totalGraphs} graphs
              </span>
            </div>
            {section.pages.map((p: any, pi: number) => (
              <PageCard
                key={p.id}
                page={p}
                pageNum={pi + 1}
                sectionRepeat={
                  repeats.find((r) => r.field && r.field !== "None")?.field ?? "None"
                }
                sectionRepeats={repeats.filter((r) => r.field && r.field !== "None").map((r) => r.field)}
                onUpdate={(d: any) => upP(p.id, d)}
                onDelete={() => delP(p.id)}
                onDuplicate={() => dupP(p.id)}
                onMoveUp={() => moveP(p.id, -1)}
                onMoveDown={() => moveP(p.id, 1)}
                isFirst={pi === 0}
                isLast={pi === section.pages.length - 1}
                theme={theme}
              />
            ))}
            <button
              onClick={addP}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 5,
                padding: "9px",
                borderRadius: 8,
                cursor: "pointer",
                background: "none",
                border: `1.5px dashed ${theme.border}`,
                color: theme.textSecondary,
                fontSize: 13,
                fontWeight: 500,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = theme.blue;
                e.currentTarget.style.color = theme.blue;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.border;
                e.currentTarget.style.color = theme.textSecondary;
              }}
            >
              <I.Plus /> Add Page
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default Section;
